import { featureFlags } from '@seen/core/featureFlags'
import {
  IconAlertTriangle,
  IconBuilding,
  IconChartBar,
  IconChefHat,
  IconClipboardText,
  IconCode,
  IconFile,
  IconFileText,
  IconInbox,
  IconList,
  IconListCheck,
  IconNotes,
  IconSparkles,
  IconTemplate,
  IconUserCog,
  IconUsersGroup,
  IconUsersPlus,
} from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export function MorePage(): ReactNode {
  const showObservations = useFeatureFlag(featureFlags.structuredCapture)
  return (
    <PageLayout title="More Pages">
      <div className="flex flex-col rounded-md border text-sm">
        <LinkRow to="https://analytics.seen.tech" external>
          <IconChartBar className="size-4 shrink-0" />
          <span>Analytics</span>
        </LinkRow>
        <LinkRow to="/document/inbox">
          <IconInbox className="size-4 shrink-0" />
          <span>Document Inbox</span>
        </LinkRow>
        <LinkRow to="https://sgv.crm.seen.tech" external>
          <IconUsersPlus className="size-4 shrink-0" />
          <span>Intake</span>
        </LinkRow>
        <LinkRow to="/care-teams">
          <IconUsersGroup className="size-4 shrink-0" />
          <span>Care Teams</span>
        </LinkRow>
        <LinkRow to="/meal-queue">
          <IconChefHat className="size-4 shrink-0" />
          <span>Meal queue</span>
        </LinkRow>
        <LinkRow to="/settings/participant-labels">
          <IconLabel />
          <span>Participant Labels</span>
        </LinkRow>
        <LinkRow to="/practitioners">
          <IconUserCog className="size-4 shrink-0" />
          <span>Practitioners</span>
        </LinkRow>
        <LinkRow to="/rt-documentation">
          <IconClipboardText className="size-4 shrink-0" />
          <span>RT Documentation</span>
        </LinkRow>
        <LinkRow to="/settings/note-labels">
          <IconLabel />
          <span>Note Labels</span>
        </LinkRow>
        <LinkRow to="/notes">
          <IconNotes className="size-4 shrink-0" />
          <span>Notes</span>
        </LinkRow>
        <LinkRow to="/settings/order-labels">
          <IconLabel />
          <span>Order Labels</span>
        </LinkRow>
        <LinkRow to="/settings/organization-conditions">
          <IconList className="size-4 shrink-0" />
          <span>Organization Conditions</span>
        </LinkRow>
        {showObservations ? (
          <LinkRow to="/settings/observations">
            <IconListCheck className="size-4 shrink-0" />
            <span>Observations</span>
          </LinkRow>
        ) : null}
        <LinkRow to="/settings/skills">
          <IconSparkles className="size-4 shrink-0" />
          <span>AI Skills</span>
        </LinkRow>
        <LinkRow to="/organizations">
          <IconBuilding className="size-4 shrink-0" />
          <span>Organizations</span>
        </LinkRow>
        <LinkRow to="/settings/task-labels">
          <IconLabel />
          <span>Task Labels</span>
        </LinkRow>
        <LinkRow to="/templates">
          <IconTemplate className="size-4 shrink-0" />
          <span>Templates</span>
        </LinkRow>
        <LinkRow to="/documents">
          <IconFile className="size-4 shrink-0" />
          <span>Uploaded Documents</span>
        </LinkRow>
        <LinkRow to="/incidents">
          <IconAlertTriangle className="size-4 shrink-0" />
          <span>Incidents</span>
        </LinkRow>
        <LinkRow to="/ccda">
          <IconFileText className="size-4 shrink-0" />
          <span>CCDA Exports</span>
        </LinkRow>
        <LinkRow to="/dev">
          <IconCode className="size-4 shrink-0" />
          <span>Dev Tools</span>
        </LinkRow>
      </div>
    </PageLayout>
  )
}

function LinkRow({
  to,
  external,
  children,
}: {
  to: string
  external?: boolean
  children: ReactNode
}): ReactNode {
  const className =
    'flex gap-2 items-center p-4 border-b transition-colors last:border-b-0 hover:bg-muted/50'
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    )
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  )
}
